import { useState, useEffect } from 'react';
import { LIGHT, MUTE, FAINT, GHOST, GOLD, EMBER, BASE, SURFACE, DISPLAY, BODY, MONO, TC } from '../lib/constants';
import { Horizon, Btn, Mo, Bd, Dp, Label, Toggle, Ic, dIc } from './shared';
import * as api from '../lib/api';

export function PaymentsScreen({ navigate }) {
  const [payments, setPayments] = useState([]);
  useEffect(() => { api.getPayments().then(r => setPayments(r.data)).catch(() => setPayments([])); }, []);

  return (
    <div data-testid="payments-screen" className="dettio-screen">
      <Horizon intensity={0.4} />
      <div className="dettio-screen__inner">
        <div className="dettio-flex dettio-flex--between dettio-flex--center" style={{ marginBottom: 20 }}>
          <div>
            <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--mute)', letterSpacing: 2, display: 'block', marginBottom: 4 }}>THIS MONTH</Mo>
            <Mo style={{ fontSize: 28, fontWeight: 700, color: 'var(--gold)' }}>${payments.reduce((s, p) => s + p.amount, 0).toLocaleString()}</Mo>
          </div>
          <button onClick={() => navigate('manual-payment')} data-testid="manual-pay-btn" style={{ padding: '10px 14px', background: GOLD, border: 'none', color: 'var(--base)', fontFamily: MONO, fontSize: 10, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.5 }}>+ LOG PAYMENT</button>
        </div>
        <Label>RECENT</Label>
        {payments.map(p => (
          <div key={p.id} onClick={() => navigate('transaction-detail', p)} data-testid={`payment-${p.id}`} className="dettio-row dettio-row--clickable">
            <div className="dettio-icon-box">
              {p.type === 'auto' ? Ic.check(16, GOLD) : Ic.coin(16, MUTE)}
            </div>
            <div className="dettio-flex-1">
              <Bd style={{ fontSize: 14, fontWeight: 600, color: 'var(--light)' }}>{p.debt_name}</Bd>
              <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block' }}>{p.date}</Mo>
                <Mo style={{ fontSize: 9, color: p.type === 'auto' ? GOLD : MUTE }}>{p.type === 'auto' ? 'AUTO' : 'MANUAL'}</Mo>
              </div>
            </div>
            <Mo style={{ fontSize: 14, fontWeight: 700, color: 'var(--gold)' }}>-${p.amount}</Mo>
          </div>
        ))}
        {payments.length === 0 && <Bd style={{ fontSize: 13, color: 'var(--mute)', textAlign: 'center', padding: 40 }}>No payments recorded yet</Bd>}
      </div>
    </div>
  );
}

export function TransactionDetailScreen({ payment }) {
  if (!payment) return null;
  const b = payment.breakdown || {};
  return (
    <div data-testid="transaction-detail" className="dettio-screen">
      <Horizon intensity={0.5} />
      <div className="dettio-screen__inner">
        <div className="dettio-screen__center" style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 12 }}>{Ic.check(40, GOLD)}</div>
          <Mo style={{ fontSize: 36, fontWeight: 700, color: 'var(--gold)', display: 'block', marginBottom: 4 }}>-${payment.amount}</Mo>
          <Bd style={{ fontSize: 14, color: 'var(--mute)', display: 'block' }}>{payment.debt_name} · {payment.date}</Bd>
        </div>
        <Label>BREAKDOWN</Label>
        {[{ l: 'Minimum', v: b.minimum || 0 }, { l: 'Extra', v: b.extra || 0 }, { l: 'Round-up', v: b.roundup || 0 }].filter(x => x.v > 0).map(s => (
          <div key={s.l} className="dettio-flex dettio-flex--between" style={{ padding: '12px 0', borderBottom: `1px solid ${FAINT}` }}>
            <Bd style={{ fontSize: 14, color: 'var(--mute)', display: 'block' }}>{s.l}</Bd>
            <Mo style={{ fontSize: 14, fontWeight: 700, color: 'var(--light)' }}>${s.v}</Mo>
          </div>
        ))}
        <Label>BALANCE CHANGE</Label>
        <div className="dettio-flex dettio-flex--between dettio-flex--center" style={{ padding: '12px 0' }}>
          <Mo style={{ fontSize: 14, color: 'var(--mute)', display: 'block' }}>${payment.balance_before?.toLocaleString()}</Mo>
          <Ic.arrow />
          <Mo style={{ fontSize: 14, fontWeight: 700, color: 'var(--gold)' }}>${payment.balance_after?.toLocaleString()}</Mo>
        </div>
      </div>
    </div>
  );
}

export function ManualPaymentScreen({ debts, onComplete }) {
  const [selDebt, setSelDebt] = useState(null);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!selDebt || !amount) return;
    setLoading(true);
    try {
      await api.createPayment({ debt_id: selDebt.id, amount: parseFloat(amount), type: 'manual' });
      setDone(true);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  if (done) return (
    <div data-testid="payment-success" style={{ padding: '60px 28px', textAlign: 'center', position: 'relative' }}>
      <Horizon intensity={0.8} />
      <div className="dettio-screen__inner">
        {Ic.check(48, GOLD)}
        <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)', display: 'block', margin: '16px 0 8px' }}>Payment Logged</Dp>
        <Mo style={{ fontSize: 28, fontWeight: 700, color: 'var(--gold)', display: 'block', marginBottom: 20 }}>-${parseFloat(amount).toLocaleString()}</Mo>
        <Btn onClick={onComplete} gold data-testid="back-to-payments-btn">BACK TO PAYMENTS</Btn>
      </div>
    </div>
  );

  return (
    <div data-testid="manual-payment-screen" className="dettio-screen">
      <Horizon intensity={0.5} />
      <div className="dettio-screen__inner">
        <Label>SELECT DEBT</Label>
        {debts.map(d => (
          <div key={d.id} onClick={() => setSelDebt(d)} data-testid={`select-debt-${d.id}`} className="dettio-row dettio-row--clickable" style={{ opacity: selDebt && selDebt.id !== d.id ? 0.4 : 1 }}>
            {dIc(d.type)}
            <div className="dettio-flex-1">
              <Bd style={{ fontSize: 14, fontWeight: 600, color: 'var(--light)' }}>{d.name}</Bd>
              <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block' }}>${d.balance.toLocaleString()}</Mo>
            </div>
            {selDebt?.id === d.id && Ic.check(16, GOLD)}
          </div>
        ))}
        {selDebt && <>
          <Label>AMOUNT</Label>
          <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" data-testid="payment-amount-input"
            style={{ width: '100%', padding: '16px', background: 'var(--faint)', border: `1px solid ${GHOST}`, color: 'var(--light)', fontFamily: MONO, fontSize: 24, fontWeight: 700, textAlign: 'center', outline: 'none', boxSizing: 'border-box', borderRadius: 8 }} />
          <div style={{ marginTop: 20 }}><Btn onClick={submit} gold disabled={!amount || loading} data-testid="submit-payment-btn">{loading ? 'LOGGING...' : 'LOG PAYMENT'}</Btn></div>
        </>}
      </div>
    </div>
  );
}

export function BadgesScreen() {
  const [badges, setBadges] = useState([]);
  useEffect(() => { api.getBadges().then(r => setBadges(r.data)).catch(() => setBadges([])); }, []);
  const earned = badges.filter(b => b.earned);
  const spendingLocked = badges.filter(b => !b.earned && b.badge_type === 'spending');
  const otherLocked = badges.filter(b => !b.earned && b.badge_type !== 'spending');

  return (
    <div data-testid="badges-screen" className="dettio-screen">
      <Horizon intensity={0.6} />
      <div className="dettio-screen__inner">
        <div className="dettio-screen__center" style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 8 }}>{Ic.trophy(32, GOLD)}</div>
          <Mo style={{ fontSize: 32, fontWeight: 700, color: 'var(--gold)', display: 'block' }}>{earned.length}</Mo>
          <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--mute)', letterSpacing: 2 }}>EARNED</Mo>
        </div>
        {earned.length > 0 && <>
          <Label>EARNED</Label>
          {earned.map(b => (
            <div key={b.id} data-testid={`badge-${b.id}`} className="dettio-row" style={{ gap: 14 }}>
              <div className="dettio-avatar" style={{ width: 42, height: 42, background: `${TC[b.tier] || GOLD}15` }}>
                {Ic.star(20, TC[b.tier] || GOLD)}
              </div>
              <div className="dettio-flex-1">
                <Bd style={{ fontSize: 15, fontWeight: 600, color: 'var(--light)' }}>{b.name}</Bd>
                <Mo style={{ fontSize: 10, color: TC[b.tier] || MUTE, display: 'block', marginTop: 2 }}>
                  {b.tier?.toUpperCase()} · {b.earned_at}
                  {b.badge_type === 'spending' && <span style={{ color: 'var(--gold)', marginLeft: 6 }}>+75 pts</span>}
                </Mo>
                {b.desc && <Bd style={{ fontSize: 11, color: 'var(--ghost)', display: 'block', marginTop: 2 }}>{b.desc}</Bd>}
              </div>
            </div>
          ))}
        </>}
        {spendingLocked.length > 0 && <>
          <Label>SPENDING IMPROVEMENT</Label>
          <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginBottom: 12, lineHeight: 1.6 }}>
            Reduce your spending and earn these badges automatically. Run Spending Analysis to check progress.
          </Mo>
          {spendingLocked.map(b => (
            <div key={b.id} data-testid={`badge-locked-${b.id}`} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 0', borderBottom: `1px solid ${FAINT}`, opacity: 0.5 }}>
              <div style={{ width: 42, height: 42, background: 'var(--faint)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{Ic.lock(18, MUTE)}</div>
              <div style={{ flex: 1 }}>
                <Bd style={{ fontSize: 15, fontWeight: 600, color: 'var(--light)' }}>{b.name}</Bd>
                <Mo style={{ fontSize: 10, color: TC[b.tier] || MUTE, letterSpacing: 1 }}>{b.tier?.toUpperCase()}</Mo>
                {b.desc && <Bd style={{ fontSize: 11, color: 'var(--ghost)', display: 'block', marginTop: 2 }}>{b.desc}</Bd>}
              </div>
              <Mo style={{ fontSize: 9, color: 'var(--gold)', fontWeight: 600 }}>+75</Mo>
            </div>
          ))}
        </>}
        {otherLocked.length > 0 && <>
          <Label>LOCKED</Label>
          {otherLocked.map(b => (
            <div key={b.id} data-testid={`badge-locked-${b.id}`} className="dettio-row" style={{ gap: 14, opacity: 0.4 }}>
              <div className="dettio-avatar" style={{ width: 42, height: 42, background: 'var(--faint)' }}>{Ic.lock(18, MUTE)}</div>
              <div className="dettio-flex-1">
                <Bd style={{ fontSize: 15, fontWeight: 600, color: 'var(--light)' }}>{b.name}</Bd>
                <Mo style={{ fontSize: 10, color: TC[b.tier] || MUTE, letterSpacing: 1 }}>{b.tier?.toUpperCase()}</Mo>
              </div>
            </div>
          ))}
        </>}
      </div>
    </div>
  );
}

export function SpendingScreen() {
  const [data, setData] = useState({ categories: [] });
  useEffect(() => { api.getSpending().then(r => setData(r.data)).catch(() => setData({ categories: [
    { name: 'Food Delivery', amount: 387 }, { name: 'Dining Out', amount: 214 },
    { name: 'Online Shopping', amount: 183 }, { name: 'Subscriptions', amount: 127 }, { name: 'Entertainment', amount: 96 }
  ] })); }, []);

  const total = data.categories.reduce((s, c) => s + c.amount, 0);
  const colors = [GOLD, EMBER, '#cd7f32', MUTE, GHOST];

  return (
    <div data-testid="spending-screen" className="dettio-screen">
      <Horizon intensity={0.5} />
      <div className="dettio-screen__inner">
        <div className="dettio-screen__center" style={{ marginBottom: 20 }}>
          <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--mute)', letterSpacing: 2, display: 'block', marginBottom: 6 }}>FEB 2026 TOTAL</Mo>
          <Mo style={{ fontSize: 32, fontWeight: 700, color: 'var(--light)' }}>${total.toLocaleString()}</Mo>
        </div>
        <div style={{ display: 'flex', height: 4, marginBottom: 24, overflow: 'hidden' }}>
          {data.categories.map((c, i) => <div key={i} style={{ flex: c.amount, background: colors[i] || GHOST }} />)}
        </div>
        {data.categories.map((c, i) => (
          <div key={i} className="dettio-row" style={{ gap: 12, padding: '12px 0' }}>
            <div style={{ width: 8, height: 8, background: colors[i] || GHOST }} />
            <Bd style={{ fontSize: 14, fontWeight: 600, color: 'var(--light)', flex: 1 }}>{c.name}</Bd>
            <Mo style={{ fontSize: 14, fontWeight: 700, color: 'var(--light)' }}>${c.amount}</Mo>
            <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block' }}>{total ? Math.round(c.amount / total * 100) : 0}%</Mo>
          </div>
        ))}
        <div style={{ marginTop: 16, padding: 14, background: `${GOLD}08`, border: `1px solid ${GOLD}15` }}>
          <Mo style={{ fontSize: 9, fontWeight: 600, color: 'var(--gold)', letterSpacing: 0.5, lineHeight: 1.6 }}>Cutting Food Delivery by 30% would free up $116/mo — that's 1 extra month off your payoff date.</Mo>
        </div>
      </div>
    </div>
  );
}

export function PremiumScreen({ user, onCheckout }) {
  const isPremium = user?.plan === 'premium';
  const features = [
    { icon: Ic.wand(18, GOLD), title: 'What-If Simulator', desc: 'Test payoff scenarios' },
    { icon: Ic.chart(18, GOLD), title: 'Spending Analysis', desc: 'See where money goes' },
    { icon: Ic.plan(18, GOLD), title: 'Budget Targets', desc: 'Set category limits' },
    { icon: Ic.flame(18, GOLD), title: 'AI Coach+', desc: 'Deeper financial coaching' },
    { icon: Ic.download(18, GOLD), title: 'Data Export', desc: 'Download your records' },
    { icon: Ic.gift(18, GOLD), title: 'Referral Rewards', desc: 'Earn credit for sharing' },
  ];

  return (
    <div data-testid="premium-screen" className="dettio-screen">
      <Horizon intensity={0.8} />
      <div className="dettio-screen__inner">
        <div className="dettio-screen__center" style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 8 }}>{Ic.star(40, GOLD)}</div>
          <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)', display: 'block', marginBottom: 6 }}>Dettio Premium</Dp>
          {isPremium ? (
            <Mo style={{ fontSize: 12, color: 'var(--gold)' }}>Active — You have full access</Mo>
          ) : (
            <Mo style={{ fontSize: 28, fontWeight: 700, color: 'var(--gold)', display: 'block' }}>$4.99<span style={{ fontSize: 12, color: 'var(--mute)' }}>/month</span></Mo>
          )}
        </div>
        <Label>INCLUDES</Label>
        {features.map((f, i) => (
          <div key={i} className="dettio-row" style={{ gap: 12, padding: '12px 0' }}>
            <div className="dettio-icon-box">{f.icon}</div>
            <div>
              <Bd style={{ fontSize: 14, fontWeight: 600, color: 'var(--light)' }}>{f.title}</Bd>
              <Bd style={{ fontSize: 11, color: 'var(--mute)', display: 'block' }}>{f.desc}</Bd>
            </div>
          </div>
        ))}
        {!isPremium && (
          <div style={{ marginTop: 20 }}><Btn onClick={onCheckout} gold data-testid="subscribe-btn">SUBSCRIBE — $4.99/MO</Btn></div>
        )}
      </div>
    </div>
  );
}

export function RoundUpsScreen() {
  const [data, setData] = useState({ enabled: true, increment: 1, current_month_total: 34.77, transactions_count: 47 });
  useEffect(() => { api.getRoundUps().then(r => setData(r.data)).catch(() => {}); }, []);

  const toggleEnabled = async () => {
    const upd = { ...data, enabled: !data.enabled };
    setData(upd);
    try { await api.updateRoundUps({ enabled: upd.enabled }); } catch (e) { console.error(e); }
  };

  return (
    <div data-testid="roundups-screen" className="dettio-screen">
      <Horizon intensity={0.5} />
      <div className="dettio-screen__inner">
        <div className="dettio-screen__center" style={{ marginBottom: 24 }}>
          <div className="glow-b" style={{ marginBottom: 8 }}>{Ic.coin(40, GOLD)}</div>
          <Mo style={{ fontSize: 32, fontWeight: 700, color: 'var(--gold)', display: 'block' }}>${data.current_month_total}</Mo>
          <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--mute)', letterSpacing: 2 }}>THIS MONTH</Mo>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          {[{ l: 'TRANSACTIONS', v: data.transactions_count }, { l: 'INCREMENT', v: `$${data.increment}` }, { l: 'STATUS', v: data.enabled ? 'ACTIVE' : 'OFF' }].map(s => (
            <div key={s.l} style={{ flex: 1, padding: 12, background: 'var(--faint)', textAlign: 'center' }}>
              <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 4 }}>{s.l}</Mo>
              <Mo style={{ fontSize: 14, fontWeight: 700, color: s.l === 'STATUS' && data.enabled ? GOLD : LIGHT }}>{s.v}</Mo>
            </div>
          ))}
        </div>
        <Label>SETTINGS</Label>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: `1px solid ${FAINT}` }}>
          <div>
            <Bd style={{ fontSize: 14, fontWeight: 600, color: 'var(--light)' }}>Round-Ups</Bd>
            <Bd style={{ fontSize: 11, color: 'var(--mute)', display: 'block' }}>Apply to every purchase</Bd>
          </div>
          <Toggle on={data.enabled} onToggle={toggleEnabled} />
        </div>
        <div style={{ marginTop: 16, padding: 14, background: `${GOLD}08`, border: `1px solid ${GOLD}15` }}>
          <Mo style={{ fontSize: 9, fontWeight: 600, color: 'var(--gold)', letterSpacing: 0.5, lineHeight: 1.6 }}>At this pace, round-ups will contribute ~$415/year toward your debt. That's like one extra monthly payment — free.</Mo>
        </div>
      </div>
    </div>
  );
}
